import logoImage from '../../assets/images/10x10icon.png';
import backgroundImage from '../../assets/images/background.jpg';
import {
  YES
} from '../../lib/Boolean.lib';
import Core from '../../lib/Core';
import {
  ACCOUNT_ACTION__LIST_JOBS
} from '../../lib/Definition';
import globalErrorHandler from '../../lib/Error/globalErrorHandler.fun';
import Google from '../../lib/Google';
import useState from '../../lib/hooks/useState.hook';
import {
  Obj
} from '../../lib/Object.lib';
import {
  exchangeJWTToken,
  login,
  URL__JWT__EXPIRED,
  URL__JWT__INVALID,
  URL__JWT__SESSION,
  verify2FACode
} from '../../lib/services/Accounts/Session.lib';
import {
  trim
} from '../../lib/String.lib';
import {
  matchLocation
} from '../../lib/URL.lib';
import {
  getVariableFromURLQuery,
  setURLQueryString
} from '../../lib/URLQuery.lib';
import Box from '../Layout/Wrappers/Box';
import Button from '../Layout/Wrappers/Button';
import Divider from '../Layout/Wrappers/Divider';
import IconButton from '../Layout/Wrappers/IconButton';
import Navigate from '../Layout/Wrappers/Navigate';
import NavLink from '../Layout/Wrappers/NavLink';
import Paper from '../Layout/Wrappers/Paper';
import TextField from '../Layout/Wrappers/TextField';
import Typography from '../Layout/Wrappers/Typography';
import Loader from '../Shared/Loader';
import TwoFactorInput from './TwoFactoreInput';

// Expiration used for normal and 2FA login
const SECONDS__2_WEEKS = 60 * 60 * 24 * 7 * 2; // 2 weeks in seconds
const EXPIRATION__LOGIN = SECONDS__2_WEEKS;

export const loginUI = {
  errorText: 'This field is required'
};

export default function Login(props) {

  const [state, updateState] = useState({
    email: '',
    password: '',
    confirm: '',
    error: {
      email: false,
      password: false
    }
  });

  const doLogin = async (event) => {
    try {
      const inputs = {
        email: trim(state.email).toLowerCase(),
        password: trim(state.password),
        ttl: EXPIRATION__LOGIN,
      };
      if (!String(inputs['email']).trim().length) {
        state.error.email = loginUI.errorText;
        return await updateState({ error: state.error });
      }
      if (!String(inputs['password']).trim().length) {
        state.error.password = loginUI.errorText;
        return await updateState({ error: state.error });
      }
      const response = Obj(
        await login({ ...inputs })
      );
      if (YES(response.twoFactorRequired)) {
        updateState({
          twoFactorRequired: true
        });
      }
    }
    catch (error) {
      Core.showError(error);
    }
  };

  const verify2FA = async (twoFactorCode = state.twoFactorCode) => {
    try {
      await verify2FACode({
        email: trim(state.email).toLowerCase(),
        code: trim(twoFactorCode),
        ttl: EXPIRATION__LOGIN,
      });
    }
    catch (error) {
      globalErrorHandler(error);
    }
  };

  const isV3Enabled = (Core.getUIVersion() === 3);
  const jwt = getVariableFromURLQuery('jwt');
  const employerId = getVariableFromURLQuery('employerId');
  const engagementId = getVariableFromURLQuery('engagementId');
  const jobId = getVariableFromURLQuery('jobId');
  const error = trim(getVariableFromURLQuery('error'));
  if (error) {
    try {
      const _error = JSON.parse(decodeURIComponent(error))
      Core.showError(_error.message || _error);
    }
    catch { }
  }

  if (Core.isLogged()) {
    if (Core.isAdmin() && employerId) {
      return (
        <Navigate
          to={setURLQueryString({
            url: '/employer/pendings',
            update: { employerId, engagementId, jobId },
            exact: false
          })}
        />
      )
    }
    if (Core.isEmployer()) {
      return (
        <Navigate
          to={setURLQueryString({
            url: '/employer/pendings',
            update: { engagementId, jobId },
            exact: false
          })}
        />
      )
    }
    if (Core.isTrusted()) {
      return isV3Enabled ? <Navigate to="/v3/home" /> : <Navigate to="/home" />
    }
    if (Core.isLimited()) {
      return isV3Enabled ? <Navigate to="/v3/jobs" /> : <Navigate to="/jobs" />
    }
    if (Core.isAdmin({ action: ACCOUNT_ACTION__LIST_JOBS })) {
      return isV3Enabled ? <Navigate to="/v3/jobs" /> : <Navigate to="/jobs" />
    }
  }
  else if (jwt) {
    exchangeJWTToken({ employerId, jwt });
    return <Loader />;
  }
  else if (matchLocation(/jwt\/(session|expired)/i)) {
    return (
      <Navigate
        to={String(URL__JWT__SESSION).replace('#/', '')}
      />
    );
  }
  else if (matchLocation(/employer\/pendings/i)) {
    return (
      <Navigate
        to={String(URL__JWT__EXPIRED).replace('#/', '')}
      />
    );
  }

  return (
    <LoginLayout>
      {state.twoFactorRequired ? (
        <>

          {/** TWO FACTOR CODE INPUT */}
          <TwoFactorInput
            email={state.email}
            onSubmit={verify2FA}
          />

          {/** RESEND CODE */}
          <Button flat small
            label='Resend Verification Code'
            onClick={async (event) => {
              try {
                Core.showLoader('Sending verification code...');
                await doLogin();
                Core.showMessage('A new verification code has been sent to your email');
              }
              catch (error) {
                globalErrorHandler(error);
              }
            }}
            className='f-sm c-purple mt-1'
          />

          {/** SIGN IN WITH ANOTHER ACCOUNT */}
          <Button flat small
            icon='logout'
            label='Sign in with another account'
            onClick={async (event) => {
              updateState({
                twoFactorRequired: false,
                email: '',
                password: ''
              });
            }}
            className='f-xs c-purple mt-1'
          />

        </>
      ) : (
        <>

          {/** EMAIL */}
          <TextField disabledBlurExport required autoFocus
            placeholder='Email Address*'
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            value={state.email}
            onChange={async (event, email) => {
              state.error.email = false;
              await updateState({ email, error: state.error });
            }}
            errorText={state.error.email}
          />
          <br />
          <Box row
            className='mt-1'
          >

            {/** PASSWORD */}
            <TextField disabledBlurExport required autoFocus
              placeholder='Password*'
              id="password"
              type={state.showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="password"
              value={state.password}
              onChange={async (event, password) => {
                state.error.password = false;
                await updateState({ password, error: state.error });
              }}
              errorText={state.error.password}
            />

            {/** SHOW/HIDE PASSWORD */}
            <IconButton
              icon={!state.showPassword ? 'visibility' : 'visibility_off'}
              onClick={() => {
                updateState({ showPassword: !state.showPassword });
              }}
              style={{
                zIndex: 2,
                marginLeft: -42,
              }}
            />

          </Box>

          {/** LOGIN ACTION */}
          <Button
            label="LOG IN"
            className="mt-3"
            onClick={async (event) => {
              Core.showLoader('Login...');
              await doLogin();
              Core.hideMessage();
            }}
            fullWidth
          />

          <Box row className='my-2'>
            <Divider className='flex-1' />
            <Typography
              className='mx-1 c-gray'
            >
              OR
            </Typography>
            <Divider className='flex-1' />
          </Box>

          {/** GOOGLE LOGIN ACTION */}
          <Button fullWidth outlined
            style={{
              borderColor: 'var(--blackLighter)',
            }}
            label={
              <Box row>
                <svg
                  width="18"
                  height="18"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                >
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                <Typography
                  className='ml-1 c-gray'
                >
                  Sign in with Google
                </Typography>
              </Box>
            }
            onClick={() => Google.connect()}
          />
          <Typography error
            acl={matchLocation(URL__JWT__INVALID)}
            className='w-100 align-center'
          >
            Invalid session
          </Typography>

          {/** FORGOT PASSWORD ACTION */}
          <NavLink
            to='/forgot/password'
            className='flex-align-center mt-2'
          >
            Forgot Password?
          </NavLink>

        </>
      )}

      {/** DISCLAIMER */}
      <Typography xs alignCenter
        className="c-black-dark mt-2 align-center"
      >
        By signing in, you agree to 10x10's Terms of Service and
        Privacy Policy. You also agree to receive subsequent email
        and third-party communications, which you may opt out of at
        any time.
      </Typography>
    </LoginLayout>
  );
}

export function LoginLayout({ children, ...props }) {
  return (
    <Box column
      className='vh-100 vw-100'
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/** HEADER */}
      <Box row className="bg-cyan p-1 px-2">
        <img alt="Logo" src={logoImage} className='mx-2' style={{ height: 26 }} />
      </Box>

      {/** CONTENT */}
      <Box row flex1 className="flex-align-center">
        <Paper
          className="p-3 flex-column flex-align-center"
          style={{
            width: sizes.m3,
            marginTop: sizes.m1
          }}
        >
          <strong className="c-black-darker mb-4 center" style={{ fontSize: 28 }}>
            Say “You’re Hired” to Every Single Candidate
          </strong>
          {children}
        </Paper>
      </Box>

      {/** FOOTER */}
      <div className="login-footer bg-black-dark">
        <div className="inline-blocks">
          <Button flat
            label="Privacy Policy"
            href="https://www.iubenda.com/privacy-policy/73371936"
            className="footer-button underlined"
          />
          <span className="ui-m-min">|</span>
          <Button flat
            label="Terms & Conditions"
            href="http://www.go10x10.com.s3-website-us-east-1.amazonaws.com/"
            className="footer-button underlined"
          />
        </div>
      </div>
    </Box>
  );
}